import MarkerClusterer from '@google/markerclustererplus';
import { renderListItemForDistributorLocation } from './distributor-render-list-item';
import { getMapIconForDistributor } from './distributor-map-icon';
import type { DistributorItem } from './distributor-find';
import { renderDistributorMapInfoWindow } from './distributor-render-info-window';
type LatLng = google.maps.LatLng;
type MapOptions = google.maps.MapOptions;
type LatLngLiteral = google.maps.LatLngLiteral;

const scriptSelfUrl = new URL(import.meta.url);

export interface MapLocation extends DistributorItem {
  marker?: google.maps.Marker;
}

/**
 * This class encapsulates the handling of the map. It performs the map initialization and updates.
 *
 * TODO: Use classes instead of ids. This is part of a content element, so there could be
 *   multiple of them on the same page. We should support this cases.
 */
export class DistributorMap {
  private readonly map: google.maps.Map;
  private locations: MapLocation[];
  private bounds: google.maps.LatLngBounds;
  private markerClusterer?: MarkerClusterer;
  private infoWindows: google.maps.InfoWindow[];
  private lastAddressQuery: string;
  private ownPositionMarker?: google.maps.Marker;
  private locationName?: string;

  constructor(
    private readonly mapElement: HTMLElement,
    initialMapSettings: MapOptions,
  ) {
    this.map = new window.google.maps.Map(this.mapElement, initialMapSettings);
    this.locations = [];
    this.bounds = new window.google.maps.LatLngBounds();
    this.infoWindows = [];
    this.lastAddressQuery = '';
  }

  /**
   * Render the HTML for the information window for the given location
   */
  private renderLocationInfoWindowContent(location: MapLocation) {
    return renderDistributorMapInfoWindow(
      location,
      (skillIdentifier) => this.mapElement.dataset[skillIdentifier] ?? '',
    );
  }

  public setLastAddressQuery(str: string): void {
    this.lastAddressQuery = str;
  }

  /**
   * Clear all old locations and load the new locations into the map.
   */
  loadLocations(
    locations: MapLocation[],
    locationName?: string,
    additionalBoundsCoordinates?: LatLng | LatLngLiteral,
  ): void {
    // Clear all previously inserted markers
    this.locations.forEach((location) => {
      location.marker?.setMap(null);
    });

    // Clear the bounds
    this.bounds = new window.google.maps.LatLngBounds();

    this.locations = locations;
    this.locationName = locationName;

    if (additionalBoundsCoordinates) {
      this.bounds.extend(additionalBoundsCoordinates);
    }

    // Set a marker for every location
    const markers = this.locations.map((location) => {
      const pos = new window.google.maps.LatLng(
        parseFloat(location.Location__c.latitude),
        parseFloat(location.Location__c.longitude),
      );

      location.marker = new window.google.maps.Marker({
        position: pos,
        icon: {
          url: getMapIconForDistributor(location, false),
          scaledSize: new window.google.maps.Size(45, 45),
        },
      });

      this.addInfoWindow(location);
      this.bounds.extend(pos);

      return location.marker;
    });

    // Clear the cluster
    this.markerClusterer?.clearMarkers();

    // initialize the new Marker Cluster
    const publicExtensionAssetPath = scriptSelfUrl.pathname.substring(
      0,
      scriptSelfUrl.pathname.indexOf(
        '/',
        scriptSelfUrl.pathname.indexOf('/_assets/') + '/_assets/'.length,
      ),
    );
    const imagePath = `${publicExtensionAssetPath}/Icons/MarkerClusterer/m`;
    this.markerClusterer = new MarkerClusterer(this.map, markers, {
      imagePath,
    });

    // adjust center of map
    this.updateMapState();
    this.renderSidebar();
  }

  public updateMapState(): void {
    this.map?.setCenter(this.bounds.getCenter());
    this.map?.setZoom(
      this.locations.length > 1
        ? this.getBoundsZoomLevel({
            height: this.mapElement.clientHeight,
            width: this.mapElement.clientWidth,
          })
        : 13,
    );

    if (this.locationName) {
      const responseTextElement = document.getElementById(
        'distributor-search-filter__response-text',
      )!!;
      responseTextElement.innerText = (
        responseTextElement.dataset['textTemplate'] ?? ''
      )
        .replaceAll('%PARTNER_AMOUNT%', this.locations.length.toString())
        .replaceAll('%ADDRESS%', this.locationName);
    }
  }

  public updateOwnPositionMarker(coordinates: LatLng): void {
    this.ownPositionMarker?.setMap(null);
    delete this.ownPositionMarker;

    this.ownPositionMarker = new window.google.maps.Marker({
      position: coordinates,
      map: this.map,
      title: this.mapElement.getAttribute('data-current-position'),
    });
  }

  private renderSidebar(): void {
    const list = document.getElementById('distributor-search-list')!!;

    list.innerHTML = '';
    this.locations.forEach((location) => {
      const element = renderListItemForDistributorLocation(
        location,
        this.lastAddressQuery,
      );

      element.addEventListener('click', () => {
        this.findMapLocationById(location.Id);
      });

      list.appendChild(element);
    });

    const firstLocationId = this.locations[0]?.Id;
    if (firstLocationId !== undefined) {
      this.findMapLocationById(firstLocationId);
    }
  }

  /**
   * Helper function to find the given location in the map and zoom in on the found object.
   */
  private findMapLocationById(locationId: string): void {
    const location = this.locations.find(
      (location) => location.Id === locationId,
    );

    if (
      location &&
      location.marker &&
      location.Location__c.latitude &&
      location.Location__c.longitude
    ) {
      this.map?.setCenter({
        lat: parseFloat(location.Location__c.latitude),
        lng: parseFloat(location.Location__c.longitude),
      });
      this.map?.setZoom(15);
      window.google.maps.event.trigger(location.marker, 'click');
    }
  }

  /**
   * Add the info window for a given location.
   */
  private addInfoWindow(location: MapLocation): void {
    const infoWindow = new window.google.maps.InfoWindow({
      content: this.renderLocationInfoWindowContent(location),
      ariaLabel: location.Name,
    });

    this.infoWindows.push(infoWindow);

    location.marker?.addListener('click', () => {
      // Toggle Marker Icon
      this.locations.forEach((someLocation) => {
        someLocation.marker?.setIcon({
          url: getMapIconForDistributor(someLocation, false),
          scaledSize: new window.google.maps.Size(45, 45),
        });
      });

      location.marker?.setIcon({
        url: getMapIconForDistributor(location, true),
        scaledSize: new window.google.maps.Size(45, 45),
      });

      // Toggle Information Window
      this.infoWindows.forEach((someInfoWindow) => {
        someInfoWindow.close();
      });
      infoWindow.open(this.map, location.marker);
    });

    infoWindow.addListener('closeclick', () => {
      location.marker?.setIcon({
        url: getMapIconForDistributor(location, false),
        scaledSize: new window.google.maps.Size(45, 45),
      });
    });
  }

  /**
   * Calculate the right zoom level based on the delivered marker list
   *
   * @see https://stackoverflow.com/questions/6048975/google-maps-v3-how-to-calculate-the-zoom-level-for-a-given-bounds
   */
  private getBoundsZoomLevel(mapDimensions: {
    height: number;
    width: number;
  }): number {
    const WORLD_DIM = { height: 256, width: 256 };
    const ZOOM_MAX = 21;

    function latRad(lat: number) {
      const sin = Math.sin((lat * Math.PI) / 180);
      const radX2 = Math.log((1 + sin) / (1 - sin)) / 2;
      return Math.max(Math.min(radX2, Math.PI), -Math.PI) / 2;
    }

    function zoom(mapPx: number, worldPx: number, fraction: number) {
      return Math.floor(Math.log(mapPx / worldPx / fraction) / Math.LN2);
    }

    const ne = this.bounds.getNorthEast();
    const sw = this.bounds.getSouthWest();
    const latFraction = (latRad(ne.lat()) - latRad(sw.lat())) / Math.PI;
    const lngDiff = ne.lng() - sw.lng();
    const lngFraction = (lngDiff < 0 ? lngDiff + 360 : lngDiff) / 360;
    const latZoom = zoom(mapDimensions.height, WORLD_DIM.height, latFraction);
    const lngZoom = zoom(mapDimensions.width, WORLD_DIM.width, lngFraction);

    return Math.min(latZoom, lngZoom, ZOOM_MAX);
  }
}
